import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHydrated } from "remix-utils/use-hydrated";

import { useFormContext } from "@rvf/react-router";

import { Form } from "~/components/forms/Form";
import { LoadingButton } from "~/components/ui/loading-button";
import { BoxedField } from "~/hec/components/molecules/boxed-field/boxed-field";
import { ClaimResponseMessage } from "~/hec/components/molecules/claim-response-message/claim-response-message";
import {
  PARTNER_FORM_ENDPOINT,
  livePartnerEmailError,
} from "~/hec/partner-form/partner-form.logic";
import { partnerFormValidator } from "~/hec/partner-form/partner-form.schemas";
import type { PartnerFormStatus } from "~/hec/partner-form/types";
import { usePartnerForm } from "~/hec/partner-form/use-partner-form";
import { cn } from "~/lib/ui";

import { resolvePartnerFormCopy } from "./copy";
import messages from "./messages";
import type {
  PartnerFormEditorState,
  PartnerFormEmailFieldProps,
  PartnerFormProps,
} from "./types";

/**
 * Organism: the Become a Partner form (COM-737). There is no legacy form to
 * port — the live Fastr page draws one as an image — so the design is that
 * drawing (white boxed fields on a light-grey panel, a white full-width
 * "Contact Our Team" button) and the behaviour is the ticket's. Validation is
 * RVF over the shared `partnerFormValidator`; `usePartnerForm` owns the send.
 *
 * Timing matches Contact Us: the whole form checks on Submit only, and Email
 * alone checks its FORMAT live. After a successful send the fields clear and
 * the form stays locked until a reload (AC).
 */
const SUBMIT_ONLY = {
  initial: "onSubmit",
  whenTouched: "onSubmit",
  whenSubmitted: "onSubmit",
} as const;

const EDITOR_STATUS: Record<PartnerFormEditorState, PartnerFormStatus> = {
  ready: { kind: "idle" },
  errors: { kind: "idle" },
  submitting: { kind: "submitting" },
  success: { kind: "succeeded" },
  failure: { kind: "failed", reason: "unavailable" },
};

/** Editor "errors" preview: validates the empty form once, showing the real Submit-time copy. */
function EditorErrorPreview() {
  const form = useFormContext();
  useEffect(() => {
    void form.validate();
    // Mount-only: the form is re-keyed when the preview state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/** Email with its live format check — see `ContactUsEmailField` for why it is its own message. */
function PartnerFormEmailField({
  label,
  disabled,
}: PartnerFormEmailFieldProps) {
  const [liveError, setLiveError] = useState<string | null>(null);
  return (
    <BoxedField
      name="email"
      type="email"
      label={label}
      required
      autoComplete="email"
      disabled={disabled}
      liveError={liveError}
      onChange={e => setLiveError(livePartnerEmailError(e.target.value))}
      data-testid="partner-form-email-input"
    />
  );
}

export function PartnerForm({
  copy,
  theme = "dark",
  editorState,
  className,
  ...rest
}: PartnerFormProps) {
  const intl = useIntl();
  const hydrated = useHydrated();
  const inEditor = editorState !== undefined;
  const partner = usePartnerForm({ enabled: !inEditor });
  const status = inEditor ? EDITOR_STATUS[editorState] : partner.status;
  const text = resolvePartnerFormCopy(copy);
  const submitting = status.kind === "submitting";
  const locked = status.kind === "succeeded";
  const fieldsDisabled = submitting || locked;
  const label = (descriptor: (typeof messages)[keyof typeof messages]) =>
    intl.formatMessage(descriptor);
  const messageTone = theme === "dark" ? "text-white" : undefined;

  return (
    <div
      className={cn("w-full font-grotesque", className)}
      data-testid="partner-form"
      {...rest}
    >
      <Form
        // Remounted after the successful send: clears every field (AC). The
        // editor state is in the key so its error preview re-seeds.
        key={`${partner.formKey}:${editorState ?? "live"}`}
        validator={partnerFormValidator}
        // Only reached before hydration (and Submit is disabled then): a native
        // submit must POST to the action, never GET the page with the
        // submitter's details in the query string.
        method="post"
        action={PARTNER_FORM_ENDPOINT}
        validationBehaviorConfig={SUBMIT_ONLY}
        handleSubmit={partner.submit}
        trackingFormName="becomeAPartner"
        noValidate
        data-testid="partner-form-root"
      >
        {editorState === "errors" && <EditorErrorPreview />}
        <fieldset
          disabled={fieldsDisabled}
          // The drawn design: a light-grey rounded panel, white boxes 15px apart.
          className="flex flex-col gap-[0.9375rem] rounded-lg bg-neutral-3 p-[1.625rem] lg:px-[1.625rem] lg:py-[1.875rem]"
        >
          <BoxedField
            name="fullName"
            label={label(messages.fullName)}
            required
            autoComplete="name"
            disabled={fieldsDisabled}
            data-testid="partner-form-full-name-input"
          />
          <BoxedField
            name="companyName"
            label={label(messages.companyName)}
            required
            autoComplete="organization"
            disabled={fieldsDisabled}
            data-testid="partner-form-company-name-input"
          />
          <BoxedField
            name="jobTitle"
            label={label(messages.jobTitle)}
            autoComplete="organization-title"
            disabled={fieldsDisabled}
            data-testid="partner-form-job-title-input"
          />
          <PartnerFormEmailField
            label={label(messages.email)}
            disabled={fieldsDisabled}
          />
          <BoxedField
            name="phone"
            label={label(messages.phone)}
            required
            mask="us-phone"
            disabled={fieldsDisabled}
            data-testid="partner-form-phone-input"
          />
          <BoxedField
            as="textarea"
            name="message"
            label={label(messages.message)}
            disabled={fieldsDisabled}
            data-testid="partner-form-message-input"
          />
        </fieldset>

        <div className="mt-6 lg:mt-12">
          <LoadingButton
            type="submit"
            variant="secondary"
            size="fit"
            // Before hydration the button is inert but keeps its live colours,
            // so SSR never flashes a greyed-out form.
            className={cn(
              "h-[2.875rem] w-full rounded-[0.1875rem] border-white px-9 text-lg font-semibold",
              !hydrated &&
                "disabled:border-white disabled:bg-white disabled:text-deep-blue",
            )}
            disabled={!hydrated || fieldsDisabled}
            isLoading={submitting}
            data-testid="partner-form-submit-button"
          >
            {text.submitLabel}
          </LoadingButton>
          {status.kind === "succeeded" && (
            <ClaimResponseMessage
              tone="success"
              format="text"
              message={text.successMessage}
              className={cn("mt-3 text-sm text-success-11", messageTone)}
              data-testid="partner-form-response-message"
            />
          )}
          {status.kind === "failed" && (
            <ClaimResponseMessage
              tone="error"
              format="text"
              message={
                status.reason === "rate_limited"
                  ? intl.formatMessage(messages.rateLimited)
                  : text.failureMessage
              }
              className={cn("mt-3 text-sm text-error", messageTone)}
              data-testid="partner-form-response-message"
            />
          )}
        </div>
      </Form>
    </div>
  );
}
